import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { BranchRow } from "@/modules/branches/domain/types";
import { RowActions } from "@/shared/ui/RowActions";
import { confirm } from "@/shared/lib/confirm";
import { DataTable, createSortableColumn, TanstackReactTable } from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";



import { ClockIcon, EditIcon, MapPinIcon, TrashIcon } from "lucide-react";

const ADDRESS_MAX = 40;

function truncateAddress(s: string | null): string {
  if (!s) return "–";
  return s.length <= ADDRESS_MAX ? s : `${s.slice(0, ADDRESS_MAX)}…`;
}

type BranchesTableProps = {
  data: BranchRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (row: BranchRow) => void;
  onDelete: (id: string) => void;
  onViewMap: (row: BranchRow) => void;
  onSchedule: (row: BranchRow) => void;
};

export function BranchesTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDelete,
  onViewMap,
  onSchedule,
}: BranchesTableProps) {
  const { has } = usePermissions();

  const columns: TanstackReactTable.ColumnDef<BranchRow>[] = [
    createSortableColumn<BranchRow>("code", "ລະຫັດ", { size: 80 }),
    createSortableColumn<BranchRow>("name", "ຊື່", { size: 120 }),
    createSortableColumn<BranchRow>("phone", "ເບີ", { size: 110 }),
    {
      id: "address",
      header: "ທີ່ຢູ່",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {truncateAddress(row.original.address)}
        </span>
      ),
      size: 180,
    },
    {
      id: "serviceRadiusM",
      header: "ລັດສະໝີ (ມ.)",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.serviceRadiusM != null
            ? row.original.serviceRadiusM.toLocaleString()
            : "–"}
        </span>
      ),
      size: 90,
    },
    createSortableColumn<BranchRow>("timezone", "ເວລາ", { size: 120 }),
    {
      id: "isActive",
      header: "ສະຖານະ",
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge variant="default">ເປີດ</Badge>
        ) : (
          <Badge variant="secondary">ປິດ</Badge>
        ),
      size: 80,
    },
    {
      id: "onlineOrdersEnabled",
      header: "ອອນໄລນ",
      cell: ({ row }) =>
        row.original.onlineOrdersEnabled ? (
          <Badge variant="outline">ຮັບອໍເດີ</Badge>
        ) : (
          <Badge variant="secondary">ບໍ່ຮັບ</Badge>
        ),
      size: 90,
    },
    {
      size: 120,
      id: "actions",
      cell: ({ row }: { row: { original: BranchRow } }) => {
        const id = row.original.id;
        const actions: {
          label: string;
          icon?: React.ReactNode;
          variant?: "destructive";
          onClick: () => void;
        }[] = [];

        if (has("branches:read"))
          actions.push({
            label: "ເບິ່ງແຜນທີ່",
            icon: <MapPinIcon className="h-4 w-4" />,
            onClick: () => onViewMap(row.original),
          });
        if (has("branches:update")) {
          actions.push({
            label: "ເວລາ & Slot",
            icon: <ClockIcon className="h-4 w-4" />,
            onClick: () => onSchedule(row.original),
          });
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          });
        }
        if (has("branches:delete"))
          actions.push({
            label: "ລຶບ",
            variant: "destructive",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບສາຂາ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບສາຂານີ້?",
                actionText: "ລຶບ",
                ActionProps: { variant: "destructive" },
              });
              if (ok) onDelete(id);
            },
          });

        return <RowActions actions={actions} maxInline={3} />;
      },
    },
  ];

  return (
    <DataTable<BranchRow, unknown>
      noDataMessage="ບໍ່ພົບສາຂາ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
